import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCustomDialog } from '../../context/DialogContext';
import '../../styles/admin.css';

const BASE = 'https://menu.searchisend.in';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
};

const LoyaltyProgram = ({ token, features }) => {
    const isMobile = useIsMobile();
    const { alert, confirm } = useCustomDialog();
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({
        loyalty_enabled: 0,
        points_per_rupee: 1,
        rupees_per_point: 0.10,
        min_redemption_points: 100
    });
    const [members, setMembers] = useState([]);
    const [saving, setSaving] = useState(false);

    const inputStyle = { width: '100%', padding: '12px', marginBottom: '20px', border: '1px solid var(--border-color)', borderRadius: '5px', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', marginBottom: '8px', fontWeight: '500' };
    const primaryBtnStyle = { backgroundColor: 'var(--primary-orange)', color: 'white', border: 'none', padding: '12px 30px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer', fontSize: '1.1em' };
    const smallPrimaryBtnStyle = { backgroundColor: 'var(--primary-orange)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer', fontSize: '0.95em' };
    const secondaryBtnStyle = { backgroundColor: 'var(--header-actions-bg)', color: 'var(--text-color)', border: '1px solid var(--border-color)', padding: '6px 12px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer', fontSize: '0.85em' };
    const sectionContainerStyle = { borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' };
    const sectionHeaderStyle = { marginTop: '0', color: 'var(--text-color-headings)', fontSize: '1.2em', marginBottom: '20px' };

    // State for viewing pin
    const [viewingPinFor, setViewingPinFor] = useState(null);
    const [adminPassword, setAdminPassword] = useState('');
    const [pinData, setPinData] = useState(null);

    // State for viewing history
    const [viewingHistoryFor, setViewingHistoryFor] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    const fetchLoyaltyData = useCallback(async () => {
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_loyalty.php?action=get_admin_data`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                if (data.settings) {
                    setSettings({
                        loyalty_enabled: Number(data.settings.loyalty_enabled) || 0,
                        points_per_rupee: data.settings.points_per_rupee || 1,
                        rupees_per_point: data.settings.rupees_per_point || 0.10,
                        min_redemption_points: data.settings.min_redemption_points || 100
                    });
                }
                setMembers(data.members || []);
            }
        } catch (err) {
            console.error("Error fetching loyalty data", err);
            alert('Failed to load loyalty data', 'Error', 'danger');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const hasLoyalty = features ? Number(features.has_loyalty_system) : undefined;

    useEffect(() => {
        if (hasLoyalty !== undefined && hasLoyalty !== 1) {
            setLoading(false);
            return;
        }
        if (hasLoyalty !== undefined) {
            fetchLoyaltyData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, hasLoyalty]);

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_loyalty.php`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}` 
                },
                body: JSON.stringify({
                    action: 'save_settings',
                    ...settings
                })
            });
            const data = await res.json();
            if (data.success) {
                alert('Loyalty settings saved successfully!', 'Success', 'success');
            } else {
                alert(data.message || 'Failed to save settings', 'Error', 'danger');
            }
        } catch (err) {
            alert('Failed to save settings', 'Error', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const handleViewPin = async (memberId) => {
        if (!adminPassword) {
            alert('Please enter your admin password', 'Error', 'warning');
            return;
        }
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_loyalty.php`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}` 
                },
                body: JSON.stringify({
                    action: 'view_pin',
                    member_id: memberId,
                    password: adminPassword
                })
            });
            const data = await res.json();
            if (data.success) {
                setPinData(data.pin);
            } else {
                alert(data.message || 'Incorrect password', 'Error', 'danger');
                setAdminPassword('');
            }
        } catch (err) {
            alert('Verification failed', 'Error', 'danger');
        }
    };

    const handleResetPin = async (memberId) => {
        if (!await confirm("Are you sure you want to reset this customer's PIN? They will be issued a new one on their next order.", "Reset PIN", "danger")) {
            return;
        }
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_loyalty.php`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}` 
                },
                body: JSON.stringify({
                    action: 'reset_pin',
                    member_id: memberId
                })
            });
            const data = await res.json();
            if (data.success) {
                alert('PIN reset successfully', 'Success', 'success');
                setViewingPinFor(null);
                setPinData(null);
                setAdminPassword('');
            } else {
                alert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            alert('Reset failed', 'Error', 'danger');
        }
    };

    const handleViewHistory = async (member) => {
        setViewingHistoryFor(member);
        setHistoryData(null);
        setHistoryLoading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_loyalty.php?action=get_member_history&phone_number=${encodeURIComponent(member.phone_number)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setHistoryData(data.history || []);
            } else {
                alert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            alert('Failed to load history', 'Error', 'danger');
        } finally {
            setHistoryLoading(false);
        }
    };

    if (loading) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
    }

    if (features && Number(features.has_loyalty_system) !== 1) {
        return (
            <div className="admin-card" style={{ textAlign: 'center', padding: '60px 40px' }}>
                <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </Link>
                <div style={{ width: '72px', height: '72px', background: 'linear-gradient(135deg, #e2e8f0, #cbd5e1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <i className="fas fa-lock" style={{ fontSize: '1.8rem', color: '#64748b' }} />
                </div>
                <h2 style={{ marginBottom: '10px' }}>Premium Feature</h2>
                <p style={{ color: 'var(--text-color-secondary)', maxWidth: '400px', margin: '0 auto 28px', lineHeight: '1.65' }}>Upgrade your plan to unlock the Customer Loyalty Program and reward your most valuable customers.</p>
                <Link to="/manage_v2_secure_auth/subscription" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 28px', background: 'var(--primary-orange, #ea580c)', color: '#fff', borderRadius: '50px', fontWeight: '700', textDecoration: 'none', boxShadow: '0 4px 14px rgba(234,88,12,0.3)' }}>
                    <i className="fas fa-crown" /> Upgrade to Unlock
                </Link>
            </div>
        );
    }

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
                <i className="fas fa-arrow-left"></i> Back to Dashboard
            </Link>
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{marginTop: '0'}}>Automated Loyalty Program</h1>
                <p style={{ color: 'var(--text-color-secondary)', margin: 0 }}>Configure reward ratios and manage your loyal customers securely.</p>
            </div>

            <div style={sectionContainerStyle}>
                <h4 style={sectionHeaderStyle}>Configuration</h4>
                <form onSubmit={handleSaveSettings}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: '500' }}>
                            <input 
                                type="checkbox" 
                                style={{ transform: 'scale(1.2)' }}
                                checked={settings.loyalty_enabled === 1}
                                onChange={(e) => setSettings({ ...settings, loyalty_enabled: e.target.checked ? 1 : 0 })}
                            />
                            Enable Loyalty Program
                        </label>
                        <p style={{ fontSize: '0.9em', color: 'var(--text-color-secondary)', margin: '5px 0 0 25px' }}>If disabled, customers will not earn or be able to redeem points.</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                        <div>
                            <label style={labelStyle}>Points Earned Per ₹1 Spent</label>
                            <input 
                                type="number" 
                                step="0.01"
                                min="0.01"
                                style={inputStyle} 
                                value={settings.points_per_rupee}
                                onChange={(e) => setSettings({ ...settings, points_per_rupee: parseFloat(e.target.value) || 0 })}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>₹ Value Per Point (Redemption)</label>
                            <input 
                                type="number" 
                                step="0.01"
                                min="0.01"
                                style={inputStyle} 
                                value={settings.rupees_per_point}
                                onChange={(e) => setSettings({ ...settings, rupees_per_point: parseFloat(e.target.value) || 0 })}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Min. Points to Redeem</label>
                            <input 
                                type="number" 
                                min="1"
                                style={inputStyle} 
                                value={settings.min_redemption_points}
                                onChange={(e) => setSettings({ ...settings, min_redemption_points: parseInt(e.target.value) || 0 })}
                            />
                        </div>
                    </div>
                    
                    <div style={{ padding: '15px', backgroundColor: 'var(--header-actions-bg)', borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '20px', fontSize: '0.95em' }}>
                        <strong>Example Scenario:</strong><br/>
                        If a customer spends ₹1,000, they earn <strong>{1000 * settings.points_per_rupee} points</strong>.<br/>
                        Those points are worth <strong>₹{(1000 * settings.points_per_rupee * settings.rupees_per_point).toFixed(2)}</strong> on their next order.
                    </div>

                    <div style={{marginTop: '30px'}}>
                        <button type="submit" style={primaryBtnStyle} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </button>
                    </div>
                </form>
            </div>

            <div style={sectionContainerStyle}>
                <h4 style={sectionHeaderStyle}>Customer Database</h4>
                         {members.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-color-secondary)' }}>
                        No loyalty members found yet. Customers are enrolled automatically on their first order.
                    </div>
                ) : isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {members.map((m) => (
                            <div key={m.id} style={{ padding: '15px', backgroundColor: 'var(--card-bg-color)', border: '1px solid var(--border-color)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                    <div>
                                        <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{m.customer_name}</div>
                                        <div style={{ fontSize: '0.85em', color: 'var(--text-color-secondary)' }}>{m.phone_number}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 'bold', color: 'var(--green-accent)', fontSize: '1.1em' }}>{Number(m.points_balance).toFixed(0)} <span style={{fontSize: '0.8em'}}>pts</span></div>
                                        <div style={{ fontSize: '0.85em', color: 'var(--text-color-secondary)' }}>Earned: {Number(m.lifetime_points * settings.points_per_rupee).toFixed(0)}</div>
                                    </div>
                                </div>
                                <div style={{ fontSize: '0.85em', color: 'var(--text-color-secondary)', marginBottom: '15px' }}>Joined: {new Date(m.created_at).toLocaleDateString()}</div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button 
                                        style={{...secondaryBtnStyle, flex: 1}}
                                        onClick={() => {
                                            setViewingPinFor(viewingPinFor === m.id ? null : m.id);
                                            setPinData(null);
                                            setAdminPassword('');
                                        }}
                                    >
                                        <i className="fas fa-key" style={{ marginRight: '5px' }} /> PIN
                                    </button>
                                    <button 
                                        style={{...secondaryBtnStyle, flex: 1}}
                                        onClick={() => handleViewHistory(m)}
                                    >
                                        <i className="fas fa-history" style={{ marginRight: '5px' }} /> History
                                    </button>
                                </div>
                                {viewingPinFor === m.id && (
                                    <div style={{ marginTop: '15px', padding: '15px', backgroundColor: 'var(--header-actions-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            <input 
                                                type="password" 
                                                style={inputStyle}
                                                placeholder="Enter Admin Password"
                                                value={adminPassword}
                                                onChange={e => setAdminPassword(e.target.value)}
                                            />
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <button style={{...smallPrimaryBtnStyle, flex: 1}} onClick={() => handleViewPin(m.id)}>Verify</button>
                                                <button style={{...secondaryBtnStyle, flex: 1}} onClick={() => handleResetPin(m.id)}>Reset PIN</button>
                                            </div>
                                        </div>
                                        {pinData && (
                                            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff', border: '1px dashed var(--border-color)', borderRadius: '5px', textAlign: 'center' }}>
                                                Customer PIN: <strong style={{ fontSize: '1.2em', color: 'var(--primary-orange)', letterSpacing: '2px' }}>{pinData}</strong>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'var(--card-bg-color)' }}>
                            <thead>
                                <tr style={{ backgroundColor: 'var(--header-actions-bg)', textAlign: 'left' }}>
                                    <th style={{ padding: '12px', border: '1px solid var(--border-color)' }}>Customer</th>
                                    <th style={{ padding: '12px', border: '1px solid var(--border-color)' }}>Contact</th>
                                    <th style={{ padding: '12px', border: '1px solid var(--border-color)' }}>Points Available</th>
                                    <th style={{ padding: '12px', border: '1px solid var(--border-color)' }}>Total Earned</th>
                                    <th style={{ padding: '12px', border: '1px solid var(--border-color)' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.map((m) => (
                                    <React.Fragment key={m.id}>
                                        <tr>
                                            <td style={{ padding: '12px', border: '1px solid var(--border-color)' }}>
                                                <div style={{ fontWeight: 'bold' }}>{m.customer_name}</div>
                                                <div style={{ fontSize: '0.85em', color: 'var(--text-color-secondary)' }}>Joined: {new Date(m.created_at).toLocaleDateString()}</div>
                                            </td>
                                            <td style={{ padding: '12px', border: '1px solid var(--border-color)' }}>{m.phone_number}</td>
                                            <td style={{ padding: '12px', border: '1px solid var(--border-color)', fontWeight: 'bold', color: 'var(--green-accent)' }}>{Number(m.points_balance).toFixed(0)}</td>
                                            <td style={{ padding: '12px', border: '1px solid var(--border-color)', color: 'var(--text-color-secondary)' }}>{Number(m.lifetime_points * settings.points_per_rupee).toFixed(0)}</td>
                                            <td style={{ padding: '12px', border: '1px solid var(--border-color)' }}>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button 
                                                        style={secondaryBtnStyle}
                                                        onClick={() => {
                                                            setViewingPinFor(viewingPinFor === m.id ? null : m.id);
                                                            setPinData(null);
                                                            setAdminPassword('');
                                                        }}
                                                    >
                                                        <i className="fas fa-key" style={{ marginRight: '5px' }} /> PIN
                                                    </button>
                                                    <button 
                                                        style={secondaryBtnStyle}
                                                        onClick={() => handleViewHistory(m)}
                                                    >
                                                        <i className="fas fa-history" style={{ marginRight: '5px' }} /> History
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {viewingPinFor === m.id && (
                                            <tr>
                                                <td colSpan="5" style={{ padding: '15px', backgroundColor: 'var(--header-actions-bg)', border: '1px solid var(--border-color)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                        <input 
                                                            type="password" 
                                                            style={{...inputStyle, marginBottom: '0', maxWidth: '250px'}}
                                                            placeholder="Enter Admin Password to view PIN"
                                                            value={adminPassword}
                                                            onChange={e => setAdminPassword(e.target.value)}
                                                        />
                                                        <button style={smallPrimaryBtnStyle} onClick={() => handleViewPin(m.id)}>Verify</button>
                                                        <span style={{ color: 'var(--text-color-secondary)' }}>or</span>
                                                        <button style={secondaryBtnStyle} onClick={() => handleResetPin(m.id)}>Reset to New PIN</button>
                                                    </div>
                                                    {pinData && (
                                                        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff', border: '1px dashed var(--border-color)', borderRadius: '5px', display: 'inline-block' }}>
                                                            Customer PIN is: <strong style={{ fontSize: '1.2em', color: 'var(--primary-orange)' }}>{pinData}</strong>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {viewingHistoryFor && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ background: 'var(--card-bg-color)', padding: '30px', borderRadius: '16px', width: '100%', maxWidth: '600px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0 }}>Order History: {viewingHistoryFor.customer_name}</h3>
                            <button onClick={() => setViewingHistoryFor(null)} style={{ background: 'none', border: 'none', fontSize: '1.5em', cursor: 'pointer', color: 'var(--text-color)' }}>&times;</button>
                        </div>
                        
                        {historyLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
                        ) : !historyData || historyData.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-color-secondary)' }}>No orders found for this customer.</div>
                        ) : (
                            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '10px' }}>
                                {historyData.map((order, idx) => (
                                    <div key={idx} style={{ padding: '15px', border: '1px solid var(--border-color)', borderRadius: '8px', marginBottom: '10px', background: 'var(--bg-color)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                            <strong>{order.order_no}</strong>
                                            <span style={{ fontSize: '0.85em', color: 'var(--text-color-secondary)' }}>{new Date(order.created_at).toLocaleString()}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                                            <div>
                                                <div style={{ fontSize: '0.9em' }}>Total: <strong>₹{order.grand_total}</strong></div>
                                                <div style={{ fontSize: '0.85em', display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: order.order_status === 'Paid' ? '#dcfce7' : '#fef3c7', color: order.order_status === 'Paid' ? '#166534' : '#b45309', marginTop: '5px' }}>{order.order_status}</div>
                                            </div>
                                            <div style={{ textAlign: 'right', fontSize: '0.9em' }}>
                                                {Number(order.loyalty_points_redeemed) > 0 ? (
                                                    <div style={{ color: 'var(--primary-orange)' }}>Redeemed {Number(order.loyalty_points_redeemed)} pts (-₹{order.loyalty_discount})</div>
                                                ) : (
                                                    <div style={{ color: 'var(--green-accent)' }}>Earned {Number(order.grand_total * settings.points_per_rupee).toFixed(0)} pts</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoyaltyProgram;
